import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import ContactList from '../contacts/ContactList'
import ContactDetail from '../contacts/ContactDetail'
import RegionFilter from '../filters/RegionFilter'
import SpeciesFilter from '../filters/SpeciesFilter'
import ConfidenceFilter from '../filters/ConfidenceFilter'
import ReportForm from '../report/ReportForm'
import { useContactStore } from '../../store/contactStore'
import { useAuth } from '../../hooks/useAuth'

// Mirrors CAP-alert-platform's MobileSheet: `position: absolute` inside a `position: relative`
// wrapper (see App.tsx), not `fixed` — a `fixed` bottom sheet over a MapLibre/WebGL map had
// unreliable nested-scroll behavior on iOS Safari (touch swipes over the list were captured by
// the map instead of scrolling it). Filters/list/detail are only mounted while expanded, rather
// than always-mounted-but-clipped, matching the proven-working reference implementation.
export default function MobileSheet() {
  const [expanded, setExpanded] = useState(false)
  const selectedId = useContactStore((s) => s.selectedContactId)
  const { username, isAuthenticated, isAdmin, credibilityTier, logout } = useAuth()
  const [showReportForm, setShowReportForm] = useState(false)

  useEffect(() => {
    if (selectedId) setExpanded(true)
  }, [selectedId])

  // Swipe gesture on the handle: track pointer start position, commit on release.
  const dragStartY = useRef<number | null>(null)

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    dragStartY.current = e.clientY
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (dragStartY.current === null) return
    const delta = dragStartY.current - e.clientY
    dragStartY.current = null

    if (delta > 50) setExpanded(true)
    else if (delta < -50) setExpanded(false)
    else setExpanded((v) => !v)
  }

  const handlePointerCancel = () => {
    dragStartY.current = null
  }

  return (
    <div
      className={`absolute bottom-0 left-0 right-0 bg-gray-50 border-t border-gray-200 rounded-t-2xl transition-all duration-300 z-10 flex flex-col overflow-hidden ${
        // dvh, not vh — vh is sized against Safari's toolbar-collapsed ("largest") viewport, so
        // content near the bottom of a tall panel (e.g. the ledger history link) can end up
        // positioned behind the toolbar when it's showing, unreachable by scroll since the
        // panel's own box already extends past the real visible area. Same fix already applied
        // to the old Sidebar.tsx; this component just never inherited it since it was built
        // fresh mirroring CAP-alert-platform's own (also vh-based) MobileSheet.
        expanded ? 'h-[70dvh]' : 'h-36'
      }`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-center justify-between px-3 py-1.5 shrink-0">
        <button
          type="button"
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          className="flex flex-col items-center py-1 gap-1 flex-1 touch-none"
          aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
        >
          <span className="h-1.5 w-10 rounded-full bg-gray-300" />
          {!(expanded && selectedId) && (
            <span className="text-sm font-semibold text-gray-900">Biosecurity Reporter</span>
          )}
        </button>
        <div className="flex items-center flex-wrap justify-end gap-2 shrink-0 max-w-[60%]">
          <Link to="/about" className="text-xs text-gray-500 hover:text-gray-700">About</Link>
          {isAuthenticated ? (
            <>
              {isAdmin && (
                <Link to="/admin" className="text-xs text-gray-500 hover:text-gray-700">Admin</Link>
              )}
              {credibilityTier !== 'verified_professional' && (
                <Link to="/professional-tier" className="text-xs text-gray-500 hover:text-gray-700">Pro tier</Link>
              )}
              <Link to="/my-reports" className="text-xs text-gray-500 hover:text-gray-700">My reports</Link>
              <span className="text-xs text-gray-500 truncate max-w-[70px]" title={username ?? ''}>
                {username}
              </span>
              <button type="button" onClick={logout} className="text-xs text-gray-500 hover:text-gray-700">
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-xs text-gray-500 hover:text-gray-700">Sign in</Link>
              <Link to="/register" className="text-xs text-blue-600 hover:text-blue-700 font-medium">Register</Link>
            </>
          )}
        </div>
      </div>

      {/* Collapsed to a compact icon button while viewing a contact's detail, rather than a
          full-width labelled button — reclaims vertical space for the detail panel itself
          (including its ledger history section, which needs to be scrollable into view on a
          phone) without losing the ability to start a new report from here. */}
      {expanded && selectedId ? (
        <div className="px-3 pb-1.5 shrink-0">
          <button
            type="button"
            onClick={() => (isAuthenticated ? setShowReportForm(true) : undefined)}
            aria-label={isAuthenticated ? 'Report a potential contact' : 'Sign in to report a contact'}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold py-1.5 transition"
          >
            {isAuthenticated ? '+ Report a contact' : (
              <Link to="/login" className="block text-white">Sign in to report a contact</Link>
            )}
          </button>
        </div>
      ) : (
        <div className="px-3 py-2 border-b border-gray-200 shrink-0">
          <button
            type="button"
            onClick={() => (isAuthenticated ? setShowReportForm(true) : undefined)}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 transition"
          >
            {isAuthenticated ? 'Report a potential contact' : (
              <Link to="/login" className="block text-white">Sign in to report a contact</Link>
            )}
          </button>
        </div>
      )}

      {expanded && (
        <>
          {/* Filters only make sense while browsing the list — hidden while viewing one
              contact's detail so that content (including ledger history) gets the space
              instead. */}
          {!selectedId && (
            <div className="border-b border-gray-200 shrink-0">
              <RegionFilter />
              <SpeciesFilter />
              <ConfidenceFilter />
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            {selectedId ? <ContactDetail /> : <ContactList />}
          </div>
        </>
      )}

      {showReportForm && (
        <ReportForm
          onClose={() => setShowReportForm(false)}
          onSubmitted={() => setShowReportForm(false)}
        />
      )}
    </div>
  )
}
